import { Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import { CardAndRelation } from '@deckplan.io/common/decks/deck';
import { InventoryService } from '../inventory.service';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Card, InventoryCard } from '@deckplan.io/common';
import { Observable } from 'rxjs';
import { Condition } from '@deckplan.io/common/filters';
import { map } from 'rxjs/operators';
import { AlertLevel, AlertsService } from '../../common/alerts/alerts.service';
import { DecksService } from '../../decks/decks.service';
import { CardPickerComponent } from '../../cards/card-picker/card-picker.component';
import { Quality } from '@deckplan.io/common/inventory';

export interface InventoryCardDialogData {
    deckCard: CardAndRelation;
    deckId: string;
    markedOwned: boolean;
}

@Component({
    selector: 'app-inventory-card-dialog',
    templateUrl: './inventory-card-dialog.component.html',
    styleUrls: ['./inventory-card-dialog.component.scss']
})
export class InventoryCardDialogComponent {
    // Currently, this component is designed to be used in conjunction with Decks and marking the cards owned.

    // This is the card that was clicked in the Deck View
    @Input() public deckCard: CardAndRelation;
    @Input() public deckId: string;
    @Input() public markOwned: boolean;
    @Output() public deckChanged = new EventEmitter<boolean>();
    public foundInventory: InventoryCard[] = [];
    public selectedInventoryCard: InventoryCard = null;
    public creatingNewInventory = false;

    constructor(private readonly inventoryService: InventoryService,
                private readonly decksService: DecksService,
                private readonly alertsService: AlertsService,
                private readonly injector: Injector,
                private readonly dialog: MatDialog) {
        const dialogData = this.injector.get<InventoryCardDialogData>(MAT_DIALOG_DATA, null);
        if (!dialogData || !dialogData.deckCard) {
            return;
        }

        this.deckCard = dialogData.deckCard;
        this.deckId = dialogData.deckId;
        this.markOwned = dialogData.markedOwned;
        // Pull the InventoryCard from the backend to see if one already exists
        this.retrieveMatchingInventoryCards(this.deckCard, this.markOwned).subscribe((foundCards) => {
            this.foundInventory = foundCards;
            if (this.foundInventory.length === 1) {
                // Show the details on the inventory card we found, allow them to adjust the quantity, etc
                this.selectedInventoryCard = this.foundInventory[0];
            } else if (this.foundInventory.length > 1) {
                // Trigger asking the user to pick from the list of items we found
                this.launchCardPicker();
            }
            // If we get back nothing (and we fall through here) then the display shows a question to the user of if they want to use the card picker to select
            // something to add to their inventory
        });
    }

    private static constructIdQueryForCardOrInventory(fieldName: string, value: string): Condition {
        return {
            dataField: {
                title: '',
                field: fieldName,
                dataType: 'string'
            },
            operation: '=',
            value
        };
    }

    public onQuantityChange(newQuantity: number): void {
        this.selectedInventoryCard.quantity = newQuantity;
    }

    public onQualityChange(newQuality: Quality): void {
        this.selectedInventoryCard.quality = newQuality;
    }

    public onFoilChange(isFoil: boolean): void {
        this.selectedInventoryCard.isFoil = isFoil;
    }

    public saveInventoryChanges(): void {
        // Read out the values from the form in the dialog and update the selectedInventoryCard

        // Save the changes, if any, made to the inventory card
        if (!this.creatingNewInventory) {
            this.inventoryService.updateInventory(this.selectedInventoryCard)
                .subscribe((inventoryCard) => {
                    this.updateDeckForNewlyOwnedCard(inventoryCard);
                }, () => {
                    this.alertsService.alert('Failed to update your inventory!', AlertLevel.Warn);
                });
        } else {
            this.inventoryService.addToInventory([this.selectedInventoryCard]).subscribe((newCards) => {
                // There should only be one card here, since we only added one; so if we get back some other number, we have a problem
                if (newCards && newCards.length === 1) {
                    this.selectedInventoryCard = newCards[0];
                    // Now that we have created the inventory card we're going to be working with, do the actual change-out to make it be the owned
                    // card in the inventory
                    this.updateDeckForNewlyOwnedCard(this.selectedInventoryCard);
                } else {
                    this.alertsService.alert(`Error adding ${this.selectedInventoryCard.name} to your inventory; please try again`, AlertLevel.Warn);
                }
            });
        }
    }

    public cancelInventoryChanges(): void {
        // Do nothing and close out the dialog; leave prior ownership settings unchanged on the DeckCard
        this.deckChanged.emit(false);
        this.dialog.closeAll();
    }

    public pickNewInventory(): void {
        const specificInventoryCardDialog = this.dialog.open(CardPickerComponent, {
            width: '100vw',
            minWidth: '100vw',
            height: '100vh',
            minHeight: '100vh',
            disableClose: false,
            data: {
                showOwnedCardsSelector: true,
                query: InventoryCardDialogComponent.constructIdQueryForCardOrInventory('name', this.deckCard.name),
                override: true,
                searchInventory: false
            }
        });
        specificInventoryCardDialog.afterClosed().subscribe((card: Card | InventoryCard) => {
            // They've picked a card, we need to add it to their inventory here, then set this.selectedInventoryCard
            const regularCard = card as Card;
            // Save off the temporary version of the inventory card; we'll save it on "save" button click so we can not add it to their inventory if they
            // click cancel
            const inventoryCard: InventoryCard = {...regularCard, quantity: 1, inventoryId: undefined, quality: Quality.Played, isFoil: false, added: null, updated: null};
            this.creatingNewInventory = true;
            this.selectedInventoryCard = inventoryCard;
        });
    }

    private updateDeckForNewlyOwnedCard(inventoryCard: InventoryCard): void {
        const handleError = () => {
            this.alertsService.alert('Failed to update this deck with ownership!', AlertLevel.Warn);
        };

        const handleSuccess = () => {
            // When we're done, emit that the deck changed, then close the dialog
            this.deckChanged.emit(true);
            this.dialog.closeAll();
            // TODO: Should this attempt to restore the old deck card if this inner request errors out?
            //       If not, it should probably at least display a different error message. If it can attempt to restore, it should
            //       attempt to re-do, probably.
        };

        let query: Condition;

        if (this.markOwned) {
            // If we're adding a new card, we want to remove the non-inventory card, and thus should do so by card ID
            query = InventoryCardDialogComponent.constructIdQueryForCardOrInventory('id', this.deckCard.id);
        } else if (this.deckCard.hasOwnProperty('inventoryId')) {
            // If we're removing an owned card, we want to remove the card by Inventory ID
            query = InventoryCardDialogComponent.constructIdQueryForCardOrInventory('inventoryId', (this.deckCard as InventoryCard).inventoryId);
        } else {
            this.alertsService.alert('Something went wrong. Unable to identify the card in your inventory', AlertLevel.Warn);
            return;
        }

        // Replace the old card in the Deck with the new card
        this.decksService.removeCards(this.deckId, query).subscribe(() => {
            if (this.markOwned) {
                this.decksService.addInventoryCard(this.deckId, inventoryCard.inventoryId, this.deckCard.quantityInDeck)
                    .subscribe(handleSuccess, handleError);
            } else {
                this.decksService.addCard(this.deckId, this.deckCard.id, this.deckCard.quantityInDeck)
                    .subscribe(handleSuccess, handleError);
            }
        }, handleError);
    }

    private retrieveMatchingInventoryCards(card: Card | InventoryCard, markOwned: boolean): Observable<InventoryCard[]> {
        // Search for matching cards by name if we're adding a new card-- if we're removing a card, it doesn't make much sense to do
        // a name search, because we know exactly which inventory card they want to mark as unowned, so search for it by its ID
        const queryOptions = {
            pagination: {},
            query: markOwned ? InventoryCardDialogComponent.constructIdQueryForCardOrInventory('name', card.name) :
                InventoryCardDialogComponent.constructIdQueryForCardOrInventory('inventoryId', (card as InventoryCard).inventoryId)
        };

        return this.inventoryService.getInventory(queryOptions).pipe(
            map(cardsAndTotalCount => cardsAndTotalCount.inventoryCards)
        );
    }

    private launchCardPicker() {
        const cardConditions: Condition[] = this.foundInventory.map(
            (inventoryCard) => InventoryCardDialogComponent.constructIdQueryForCardOrInventory('inventoryId', inventoryCard.inventoryId)
        );

        const specificInventoryCardDialog = this.dialog.open(CardPickerComponent, {
            width: '100vw',
            minWidth: '100vw',
            height: '100vh',
            minHeight: '100vh',
            disableClose: false,
            data: {
                showOwnedCardsSelector: true,
                query: {
                    operator: 'or', operands: cardConditions
                },
                override: true,
                searchInventory: true
            }
        });
        specificInventoryCardDialog.afterClosed().subscribe((card: Card | InventoryCard) => {
            if (card.hasOwnProperty('quantity')) {
                this.selectedInventoryCard = card as InventoryCard;
            }
            // Should never hit the else case
        });
    }
}
